interface Props {
  patch: string
  className?: string
}

type Row =
  | { kind: 'header'; content: string }
  | { kind: 'hunk'; content: string }
  | { kind: 'context'; content: string }
  | { kind: 'change'; left: string | null; right: string | null }

/**
 * Side-by-side variant of {@link ./DiffView}. Same input (unified diff string),
 * different rendering: removed lines on the left, added lines on the right,
 * paired row-wise within each hunk so the user can read "before vs after" at
 * a glance. When removes and adds in a hunk are unequal, the shorter side is
 * padded with empty cells.
 */
export default function SideBySideDiffView({ patch, className }: Props) {
  const rows = parseToRows(patch.replace(/\r\n/g, '\n'))

  return (
    <div
      className={`text-[11px] font-mono leading-relaxed overflow-x-auto rounded-md bg-black/30 border border-white/5 p-3 ${className ?? ''}`}
      style={{ color: 'var(--text-primary)' }}
    >
      <table className="w-full" style={{ borderCollapse: 'collapse', tableLayout: 'fixed' }}>
        <tbody>
          {rows.map((row, i) => {
            if (row.kind === 'header') {
              return (
                <tr key={i}>
                  <td colSpan={2} style={{ ...cellBase, color: 'var(--text-muted)' }}>
                    {row.content || ' '}
                  </td>
                </tr>
              )
            }
            if (row.kind === 'hunk') {
              return (
                <tr key={i}>
                  <td colSpan={2} style={{ ...cellBase, color: 'var(--accent)', opacity: 0.85 }}>
                    {row.content || ' '}
                  </td>
                </tr>
              )
            }
            if (row.kind === 'context') {
              return (
                <tr key={i}>
                  <td style={{ ...cellBase, ...contextStyle }}>{row.content || ' '}</td>
                  <td style={{ ...cellBase, ...contextStyle }}>{row.content || ' '}</td>
                </tr>
              )
            }
            // row.kind === 'change'
            const { left, right } = row
            return (
              <tr key={i}>
                <td style={{ ...cellBase, ...(left != null ? removedStyle : emptyStyle) }}>
                  {left ?? ' '}
                </td>
                <td style={{ ...cellBase, ...(right != null ? addedStyle : emptyStyle) }}>
                  {right ?? ' '}
                </td>
              </tr>
            )
          })}
        </tbody>
      </table>
    </div>
  )
}

function parseToRows(patch: string): Row[] {
  const lines = patch.split('\n')
  const rows: Row[] = []
  const removes: string[] = []
  const adds: string[] = []

  const flush = () => {
    const n = Math.max(removes.length, adds.length)
    for (let i = 0; i < n; i++) {
      rows.push({
        kind: 'change',
        left: i < removes.length ? removes[i] : null,
        right: i < adds.length ? adds[i] : null,
      })
    }
    removes.length = 0
    adds.length = 0
  }

  for (const line of lines) {
    if (line.startsWith('---') || line.startsWith('+++')) {
      flush()
      rows.push({ kind: 'header', content: line })
    } else if (line.startsWith('@@')) {
      flush()
      rows.push({ kind: 'hunk', content: line })
    } else if (line.startsWith('-')) {
      removes.push(line)
    } else if (line.startsWith('+')) {
      adds.push(line)
    } else {
      flush()
      rows.push({ kind: 'context', content: line })
    }
  }
  flush()
  return rows
}

const cellBase: React.CSSProperties = {
  verticalAlign: 'top',
  width: '50%',
  whiteSpace: 'pre-wrap',
  wordBreak: 'break-word',
  padding: '0 4px',
}

const contextStyle: React.CSSProperties = { color: 'var(--text-secondary)' }
const removedStyle: React.CSSProperties = { color: 'var(--score-poor)', background: 'rgba(239,68,68,0.07)' }
const addedStyle: React.CSSProperties = { color: 'var(--score-good)', background: 'rgba(34,197,94,0.07)' }
const emptyStyle: React.CSSProperties = { background: 'rgba(255,255,255,0.02)' }
